import React from "react";
import {
  Phone,
  Mail,
  Trash2,
  MessageSquare,
  Loader2,
  Send,
  AlertCircle,
} from "lucide-react";
import { SERVICES } from "@/data/services";
import {
  STATUS_OPTIONS,
  statusMeta,
  isFollowupDue,
  formatContactDate,
} from "@/pages/admin/contact/constants";
import ContactRowDetails from "@/pages/admin/contact/ContactRowDetails";

/**
 * Single contact request row + its expanded details panel.
 * All callbacks are passed in by the parent ContactRequestsPanel.
 */
const ContactRow = ({
  req,
  lang,
  t,
  expanded,
  onToggleExpand,
  onStatusChange,
  onDelete,
  onSendFollowup,
  followingUp,
  notesDraft,
  setNotesDraft,
  savingNote,
  onSaveNote,
}) => {
  const meta = statusMeta(req.status);
  const due = isFollowupDue(req);
  const serviceLabel = (slug) => {
    const s = SERVICES.find((x) => x.slug === slug);
    return s ? t(`services.${s.key}.label`) : slug;
  };

  return (
    <React.Fragment>
      <tr
        data-testid={`admin-contact-row-${req.id}`}
        data-followup-due={due ? "true" : "false"}
        className="border-b border-[#F2EBE5]/5 hover:bg-[#0a0a0a] transition-colors"
      >
        <td className="px-4 py-4 text-[#A19E9C] text-xs whitespace-nowrap">
          {formatContactDate(req.created_at, lang)}
        </td>
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="font-serif-display text-lg text-[#F2EBE5]">{req.name}</div>
            {due && (
              <span
                data-testid={`admin-followup-due-${req.id}`}
                className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[#E8C66C]/60 text-[#E8C66C] bg-[#E8C66C]/5 flex items-center gap-1 whitespace-nowrap"
                title={lang === "fr" ? "Relance recommandée" : "Follow-up recommended"}
              >
                <AlertCircle className="w-3 h-3" strokeWidth={1.5} />
                {lang === "fr" ? "Relance due" : "Follow up"}
              </span>
            )}
          </div>
          {req.budget && (
            <div className="text-xs text-[#A19E9C]">
              {lang === "fr" ? "Budget : " : "Budget: "}
              {req.budget}
            </div>
          )}
        </td>
        <td className="px-4 py-4 text-[#C5A059] italic">
          {serviceLabel(req.service_type)}
        </td>
        <td className="px-4 py-4 space-y-1">
          <a
            href={`mailto:${req.email}`}
            className="flex items-center gap-2 text-xs text-[#F2EBE5] hover:text-[#C5A059]"
          >
            <Mail className="w-3 h-3" strokeWidth={1.5} />
            {req.email}
          </a>
          {req.phone && (
            <a
              href={`tel:${req.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-xs text-[#F2EBE5] hover:text-[#C5A059]"
            >
              <Phone className="w-3 h-3" strokeWidth={1.5} />
              {req.phone}
            </a>
          )}
        </td>
        <td className="px-4 py-4">
          <select
            value={req.status}
            onChange={(e) => onStatusChange(req.id, e.target.value)}
            data-testid={`admin-status-${req.id}`}
            className="bg-transparent border text-xs px-2 py-1.5 focus:outline-none transition-colors"
            style={{ borderColor: `${meta.color}55`, color: meta.color }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.v} value={s.v} className="bg-[#0a0a0a] text-[#F2EBE5]">
                {lang === "fr" ? s.fr : s.en}
              </option>
            ))}
          </select>
        </td>
        <td className="px-4 py-4 text-right">
          <div className="flex items-center justify-end gap-2">
            {req.status === "quote_sent" && (
              <button
                type="button"
                onClick={() => onSendFollowup(req.id)}
                disabled={followingUp === req.id}
                data-testid={`admin-followup-btn-${req.id}`}
                className="text-[#A19E9C] hover:text-[#E8C66C] transition-colors p-1 disabled:opacity-50"
                title={
                  req.last_followup_sent_at
                    ? lang === "fr"
                      ? "Renvoyer une relance"
                      : "Send follow-up again"
                    : lang === "fr"
                      ? "Envoyer une relance"
                      : "Send follow-up"
                }
              >
                {followingUp === req.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => onToggleExpand(req.id)}
              data-testid={`admin-expand-${req.id}`}
              className="text-[#A19E9C] hover:text-[#C5A059] transition-colors p-1"
              title={lang === "fr" ? "Voir le détail" : "View details"}
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(req.id)}
              data-testid={`admin-delete-${req.id}`}
              className="text-[#A19E9C] hover:text-red-400 transition-colors p-1"
              title={lang === "fr" ? "Supprimer" : "Delete"}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <ContactRowDetails
          req={req}
          lang={lang}
          notesDraft={notesDraft}
          setNotesDraft={setNotesDraft}
          savingNote={savingNote}
          onSaveNote={onSaveNote}
        />
      )}
    </React.Fragment>
  );
};

export default ContactRow;
